import type { PassengerTraits } from '@full-elevator/sim';
import { floorName } from '../floorNames.js';
import { COLORS, floorColorCss, PEOPLE } from '../theme.js';
import type { Uniform } from '../view/people.js';
import { hidesHair, isCourier, lookOf, UNIFORM_COAT, uniformOf } from '../view/people.js';

/**
 * A person holding their ticket, as SVG for the DOM screens — the same person,
 * look and costume the game draws (`view/figure.ts`), so the waiting guests on
 * the home screen's next floor are the ones the shift will send.
 *
 * Drawn on the mockup grid (`design/HotelE.dc.html`): a 36×84 box, the ticket
 * at y 42–70. Costumes follow ADR-0018's table and nothing else.
 */
const css = (colour: number): string => `#${colour.toString(16).padStart(6, '0')}`;
const INK = css(COLORS.ink);

const HAIR: Record<string, string> = {
  bald: '',
  short: 'M10 25 q0 -10 8 -10 q8 0 8 10 q-2 -6 -8 -6 q-6 0 -8 6 z',
  bun: 'M10 25 q0 -10 8 -10 q8 0 8 10 q-2 -6 -8 -6 q-6 0 -8 6 z M15 14 a3 3 0 1 1 6 0 a3 3 0 1 1 -6 0 z',
  long: 'M10 28 q-1 -13 8 -13 q9 0 8 13 l0 6 q-1 -12 -8 -13 q-7 1 -8 13 z',
  curly: 'M9 26 q-2 -6 2 -9 q1 -5 7 -4 q6 -1 7 4 q4 3 2 9 q-2 -5 -9 -5 q-7 0 -9 5 z',
};

interface Who {
  readonly look: ReturnType<typeof lookOf>;
  readonly traits: PassengerTraits;
  readonly courier: boolean;
  readonly impatient: boolean;
  /** A broad build, for someone taking two seats. */
  readonly wide: boolean;
  /** The ticket's box: wider for two seats, or for a transfer's two numbers. */
  readonly width: number;
}

export function personSvg(
  id: string,
  traits: PassengerTraits,
  destination: number,
  nextStop = destination,
  /** The floor at street level, which the ticket names floors from (ADR-0028). */
  ground = 0,
  /** What the ticket says, when it is more than one floor: a postman's round (ADR-0031). */
  label?: string,
): string {
  const wide = traits.size > 1;
  const transfer = nextStop !== destination;
  const who: Who = {
    look: lookOf({ id, vip: traits.vip }),
    traits,
    // A crew or a commuter carries a deadline too, but wears a uniform (ADR-0029, ADR-0031).
    courier: isCourier(traits),
    impatient: traits.patienceMs !== null && !traits.vip,
    wide,
    width: wide ? 60 : transfer ? 54 : 36,
  };
  const bodyShift = (who.width - (wide ? 60 : 36)) / 2;
  const parts = [
    `<g transform="translate(${bodyShift} 0)">${body(who).join('')}</g>`,
    `<g transform="translate(${who.width / 2 - 18} 0)">${head(who).join('')}</g>`,
    ...ticket(who, destination, nextStop, ground, label),
  ];
  return `<svg viewBox="0 0 ${who.width} 84" width="${who.width}" height="84" aria-hidden="true" overflow="visible">${parts.join('')}</svg>`;
}

function body(who: Who): string[] {
  const cx = who.wide ? 30 : 18;
  const parts: string[] = [];
  const uniform = uniformOf(who.traits);
  if (who.courier) {
    parts.push(
      `<rect x="${cx + 4}" y="37" width="12" height="18" rx="2" fill="${css(PEOPLE.parcel)}" stroke="${INK}" stroke-width="1.2"/>`,
    );
  }
  if (uniform) parts.push(...CARRIED_SVG[uniform](cx));
  if (who.traits.archetype === 'paramedic') {
    // The paramedic's bag, its cross the only mark on it (ADR-0029).
    parts.push(
      `<rect x="${cx + 4}" y="37" width="13" height="15" rx="2" fill="${css(PEOPLE.helmet)}" stroke="${INK}" stroke-width="1.2"/>`,
      `<path d="M${cx + 10.5} 40v9M${cx + 6} 44.5h9" stroke="${css(PEOPLE.medic)}" stroke-width="2.4"/>`,
    );
  }
  const legs = who.wide ? [cx - 10, cx + 4] : [cx - 6, cx + 1.5];
  for (const x of legs) {
    parts.push(
      `<rect x="${x}" y="64" width="${who.wide ? 6 : 4.5}" height="16" rx="1.5" fill="${css(PEOPLE.legs)}"/>`,
      `<ellipse cx="${x + 2.5}" cy="81" rx="${who.wide ? 5 : 4}" ry="2.2" fill="${css(PEOPLE.shoes)}"/>`,
    );
  }
  const coat = who.wide
    ? 'M8 70 Q3 48 15 40 Q30 32 45 40 Q57 48 52 70 Z'
    : 'M8 68 L9 43 Q9 36 18 36 Q27 36 27 43 L28 68 Z';
  const cloth = uniform ? UNIFORM_COAT[uniform] : who.look.coat;
  parts.push(`<path d="${coat}" fill="${css(cloth)}" stroke="${INK}" stroke-width="1.5"/>`);
  // An apron's straps, or a window cleaner's harness (ADR-0031).
  const strap =
    uniform === 'housekeeping' ? PEOPLE.apron : uniform === 'cleaner' ? PEOPLE.hardHat : null;
  if (strap !== null) {
    for (const x of [cx - 4.5, cx + 4.5]) {
      parts.push(
        `<path d="M${x} 37 V44" stroke="${css(strap)}" stroke-width="2.6" stroke-linecap="round"/>`,
      );
    }
  }
  return parts;
}

/** The head and what is on it, on the 36-wide grid. */
function head(who: Who): string[] {
  const { look, traits } = who;
  const parts: string[] = [];
  if (traits.groupId !== null) {
    parts.push(
      `<rect x="12" y="33.5" width="12" height="4" rx="2" fill="${css(PEOPLE.scarf)}" stroke="${INK}" stroke-width="1"/>`,
    );
  }
  parts.push(
    `<circle cx="18" cy="26" r="8" fill="${css(look.skin)}" stroke="${INK}" stroke-width="1.5"/>`,
  );
  const hair = HAIR[look.hair];
  const uniform = uniformOf(traits);
  if (!who.courier && !hidesHair(traits) && hair) {
    parts.push(
      `<path d="${hair}" fill="${css(look.hairColour)}" stroke="${INK}" stroke-width="1"/>`,
    );
  }
  parts.push(
    `<circle cx="15.3" cy="27" r="1" fill="${INK}"/><circle cx="20.7" cy="27" r="1" fill="${INK}"/>`,
  );
  if (who.impatient) {
    parts.push(
      `<path d="M13.2 23.2 l3.2 1.4 M22.8 23.2 l-3.2 1.4" stroke="${INK}" stroke-width="1.3" stroke-linecap="round"/>`,
    );
  }
  if (look.glasses && !who.courier) {
    parts.push(
      `<circle cx="15.3" cy="27" r="2.4" fill="none" stroke="${INK}" stroke-width="1"/><circle cx="20.7" cy="27" r="2.4" fill="none" stroke="${INK}" stroke-width="1"/>`,
    );
  }
  if (who.courier) {
    parts.push(
      `<path d="M8.5 28 q0 -13 9.5 -13 q9.5 0 9.5 13 v2 h-19 z" fill="${css(PEOPLE.helmet)}" stroke="${INK}" stroke-width="1.5"/>`,
      `<rect x="10.5" y="22" width="15" height="7" rx="3" fill="${css(PEOPLE.visor)}" stroke="${INK}" stroke-width="1"/>`,
    );
  }
  if (uniform) parts.push(...(HEADWEAR_SVG[uniform] ?? []));
  if (traits.vip) {
    parts.push(
      `<rect x="8" y="16" width="20" height="3.5" rx="1.5" fill="${css(PEOPLE.hat)}"/>`,
      `<rect x="11" y="2" width="14" height="15" rx="1.5" fill="${css(PEOPLE.hat)}"/>`,
      `<rect x="11" y="12" width="14" height="3" fill="${css(PEOPLE.hatBand)}"/>`,
    );
  }
  return parts;
}

/** What each uniform carries behind them, on the 36-wide grid (ADR-0029, ADR-0031). */
const CARRIED_SVG: Record<Uniform, (cx: number) => string[]> = {
  medic: () => [],
  police: () => [],
  postman: (cx) => [
    `<rect x="${cx + 6}" y="33" width="5" height="6" fill="${css(PEOPLE.letters)}" stroke="${INK}" stroke-width="1"/>`,
    `<rect x="${cx + 11.5}" y="34.5" width="5" height="5" fill="${css(PEOPLE.letters)}" stroke="${INK}" stroke-width="1"/>`,
    `<rect x="${cx + 4}" y="37" width="14" height="13" rx="2" fill="${css(PEOPLE.mailBag)}" stroke="${INK}" stroke-width="1.2"/>`,
  ],
  commuter: (cx) => [
    `<rect x="${cx + 4}" y="37" width="12" height="16" rx="3" fill="${css(PEOPLE.backpack)}" stroke="${INK}" stroke-width="1.2"/>`,
  ],
  housekeeping: (cx) =>
    [0, 1, 2].map(
      (i) =>
        `<rect x="${cx + 4 - i}" y="${37 + i * 4.5}" width="14" height="4.5" rx="2" fill="${css(PEOPLE.towels)}" stroke="${INK}" stroke-width="1"/>`,
    ),
  // More of each building's own (ADR-0043).
  waiter: (cx) => [
    `<path d="M${cx + 5} 45 Q${cx + 11} 36 ${cx + 17} 45 Z" fill="${css(PEOPLE.cloche)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<circle cx="${cx + 11}" cy="38.6" r="1.3" fill="${css(PEOPLE.cloche)}" stroke="${INK}" stroke-width="1"/>`,
    `<rect x="${cx + 3}" y="45" width="16" height="2.5" rx="1" fill="${css(PEOPLE.waiter)}" stroke="${INK}" stroke-width="1"/>`,
  ],
  bellhop: (cx) => [
    `<path d="M${cx + 9.5} 36 V34 H${cx + 13.5} V36" fill="none" stroke="${INK}" stroke-width="1"/>`,
    `<rect x="${cx + 6}" y="36" width="11" height="6" rx="1.5" fill="${css(PEOPLE.parcel)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<rect x="${cx + 4}" y="42" width="15" height="10" rx="2" fill="${css(PEOPLE.suitcase)}" stroke="${INK}" stroke-width="1.2"/>`,
  ],
  cyclist: (cx) => [
    `<circle cx="${cx + 11}" cy="45" r="8" fill="none" stroke="${css(PEOPLE.visor)}" stroke-width="2.2"/>`,
    `<circle cx="${cx + 11}" cy="45" r="6" fill="none" stroke="${css(PEOPLE.rim)}" stroke-width="1"/>`,
    `<path d="M${cx + 5} 45 H${cx + 17} M${cx + 11} 39 V51 M${cx + 6.8} 40.8 L${cx + 15.2} 49.2 M${cx + 15.2} 40.8 L${cx + 6.8} 49.2" stroke="${css(PEOPLE.rim)}" stroke-width="0.8"/>`,
  ],
  executive: (cx) => [
    `<path d="M${cx + 8.5} 41 V38.5 H${cx + 13.5} V41" fill="none" stroke="${INK}" stroke-width="1.2"/>`,
    `<rect x="${cx + 4}" y="41" width="14" height="10" rx="1.5" fill="${css(PEOPLE.briefcase)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<rect x="${cx + 10}" y="44" width="2" height="1.6" fill="${css(PEOPLE.hatBand)}"/>`,
  ],
  investor: (cx) => [
    `<rect x="${cx + 6}" y="37" width="11" height="4" fill="${css(PEOPLE.letters)}" stroke="${INK}" stroke-width="0.9"/>`,
    `<rect x="${cx + 4}" y="39" width="15" height="12" rx="1.5" fill="${css(PEOPLE.portfolio)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<circle cx="${cx + 11.5}" cy="45" r="1.4" fill="${css(PEOPLE.hatBand)}"/>`,
  ],
  // The hotel's departures (ADR-0051).
  traveller: (cx) => [
    `<path d="M${cx + 8} 39 V31 H${cx + 14} V39" fill="none" stroke="${INK}" stroke-width="1.2"/>`,
    `<rect x="${cx + 4}" y="39" width="14" height="15" rx="2.5" fill="${css(PEOPLE.rollingCase)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<circle cx="${cx + 7}" cy="54.5" r="1.4" fill="${INK}"/>`,
    `<circle cx="${cx + 15}" cy="54.5" r="1.4" fill="${INK}"/>`,
  ],
  robe: (cx) => [
    `<rect x="${cx + 5}" y="39" width="13" height="10" fill="${css(PEOPLE.letters)}" stroke="${INK}" stroke-width="1"/>`,
    `<path d="M${cx + 7} 42 H${cx + 16} M${cx + 7} 44.5 H${cx + 16} M${cx + 7} 47 H${cx + 16}" stroke="${INK}" stroke-width="0.7"/>`,
  ],
  wedding: (cx) => [
    `<path d="M${cx + 11} 52 V44" stroke="${css(PEOPLE.sage)}" stroke-width="2"/>`,
    `<circle cx="${cx + 8}" cy="42" r="3" fill="${css(PEOPLE.bouquet)}" stroke="${INK}" stroke-width="0.9"/>`,
    `<circle cx="${cx + 14}" cy="42" r="3" fill="${css(PEOPLE.bouquet)}" stroke="${INK}" stroke-width="0.9"/>`,
    `<circle cx="${cx + 11}" cy="39" r="3" fill="${css(PEOPLE.bouquet)}" stroke="${INK}" stroke-width="0.9"/>`,
  ],
  cleaner: (cx) => [
    `<path d="M${cx + 13} 43 L${cx + 17} 28" stroke="${INK}" stroke-width="1.6"/>`,
    `<rect x="${cx + 12.5}" y="25.5" width="10" height="2.8" rx="1" fill="${css(PEOPLE.visor)}"/>`,
    `<path d="M${cx + 4} 40 H${cx + 16} L${cx + 14.5} 52 H${cx + 5.5} Z" fill="${css(PEOPLE.bucket)}" stroke="${INK}" stroke-width="1.2"/>`,
  ],
};

/** What each uniform wears on its head, around a head at (18, 26) of radius 8. */
const HEADWEAR_SVG: Partial<Record<Uniform, string[]>> = {
  // The police cap, with its badge (ADR-0029).
  police: [
    `<path d="M9 23 L8 17 Q18 11 28 17 L27 23 Z" fill="${css(PEOPLE.policeCap)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<rect x="8.5" y="22" width="19" height="2.6" rx="1.2" fill="${css(PEOPLE.policeCap)}"/>`,
    `<circle cx="18" cy="17.6" r="1.9" fill="${css(PEOPLE.badge)}"/>`,
  ],
  // The postman's cap, an envelope for a badge (ADR-0031).
  postman: [
    `<path d="M9 23 L8 17 Q18 11 28 17 L27 23 Z" fill="${css(PEOPLE.postalCap)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<rect x="8.5" y="22" width="19" height="2.6" rx="1.2" fill="${css(PEOPLE.postalCap)}"/>`,
    `<rect x="15.6" y="15.8" width="4.8" height="3.2" fill="${css(PEOPLE.letters)}" stroke="${INK}" stroke-width="0.8"/>`,
  ],
  cleaner: [
    `<path d="M9 24 Q9 13 18 13 Q27 13 27 24 Z" fill="${css(PEOPLE.hardHat)}" stroke="${INK}" stroke-width="1.4"/>`,
    `<rect x="7" y="23" width="22" height="2.6" rx="1.2" fill="${css(PEOPLE.hardHat)}" stroke="${INK}" stroke-width="1"/>`,
    `<path d="M18 13 V22" stroke="${INK}" stroke-width="1.2"/>`,
  ],
  housekeeping: [
    `<rect x="9.8" y="19.5" width="16.4" height="3.2" rx="1.6" fill="${css(PEOPLE.apron)}" stroke="${INK}" stroke-width="1"/>`,
  ],
  // The bellhop's pillbox cap and the cyclist's helmet (ADR-0043).
  bellhop: [
    `<rect x="12" y="13" width="12" height="7" rx="1.5" fill="${css(PEOPLE.bellhopCap)}" stroke="${INK}" stroke-width="1.2"/>`,
    `<rect x="12" y="17.4" width="12" height="1.8" fill="${css(PEOPLE.hatBand)}"/>`,
  ],
  cyclist: [
    `<path d="M8.5 25 Q9 12 19 12.5 Q28 14 27.5 25 Z" fill="${css(PEOPLE.bikeHelmet)}" stroke="${INK}" stroke-width="1.4"/>`,
    `<path d="M14 14.5 V19 M18 14 V18.5 M22 14.5 V19" stroke="${INK}" stroke-width="1.1"/>`,
  ],
  // Flowers in the couple's hair (ADR-0051).
  wedding: [13, 18, 23].map(
    (x) =>
      `<circle cx="${x}" cy="17.5" r="2" fill="${css(PEOPLE.bouquet)}" stroke="${INK}" stroke-width="0.8"/>`,
  ),
  commuter: [
    `<path d="M9.2 26 Q8.5 13 18 13.5 Q27.5 13 26.8 26" fill="none" stroke="${css(PEOPLE.headphones)}" stroke-width="2"/>`,
    `<rect x="7.5" y="23.5" width="4" height="6.5" rx="1.6" fill="${css(PEOPLE.headphones)}"/>`,
    `<rect x="24.5" y="23.5" width="4" height="6.5" rx="1.6" fill="${css(PEOPLE.headphones)}"/>`,
  ],
};

/** The ticket: the game's chip, colour and number, held at both edges. */
function ticket(
  who: Who,
  destination: number,
  nextStop: number,
  ground: number,
  round?: string,
): string[] {
  const x = 4;
  const w = who.width - 8;
  const skin = css(who.look.skin);
  // The number the player drives to, as the chip in play says it.
  const label =
    round ??
    (nextStop === destination
      ? floorName(destination, ground)
      : `${floorName(nextStop, ground)}›${floorName(destination, ground)}`);
  const parts: string[] = [];
  if (who.traits.vip) {
    parts.push(
      `<rect x="${x - 3.5}" y="38.5" width="${w + 7}" height="35" rx="12" fill="none" stroke="${css(COLORS.vip)}" stroke-width="2"/>`,
    );
  }
  parts.push(
    `<rect x="${x}" y="42" width="${w}" height="28" rx="9" fill="${floorColorCss(nextStop)}" stroke="${INK}" stroke-width="1.5"/>`,
  );
  if (who.traits.groupId !== null) {
    parts.push(
      `<rect x="7" y="47" width="3" height="18" rx="1.5" fill="${css(COLORS.groupStripe)}"/>`,
    );
  }
  parts.push(
    // A round spans the whole ticket, second seat and all; a floor sits on the first seat.
    `<text x="${who.wide && !round ? who.width * 0.36 : who.width / 2}" y="61" text-anchor="middle" font-family="Baloo 2, system-ui, sans-serif" font-size="${round ? 12 : label.length > 2 ? 13 : 15}" font-weight="800" fill="${css(COLORS.chipText)}">${label}</text>`,
    `<circle cx="${x}" cy="57" r="3" fill="${skin}" stroke="${INK}" stroke-width="1"/>`,
    `<circle cx="${x + w}" cy="57" r="3" fill="${skin}" stroke="${INK}" stroke-width="1"/>`,
  );
  if (who.impatient) {
    parts.push(
      `<rect x="${x + w - 2.5}" y="50" width="5" height="3" rx="1" fill="${css(PEOPLE.hatBand)}" stroke="${INK}" stroke-width="0.8"/>`,
    );
  }
  return parts;
}
